import React, { useState } from 'react';
import { Button, Col, Row, Space, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { FeesFormInputs } from '../../Fees/modal-fees';
import { ChargesFormInputs } from '../../Charges/modal-charges';
import Limits, { ICashback, ICashOutAndLoad, IPurchase, LimitsFormInputs } from '../../Limits/limits';
import { UsageLimits } from '../../UsageLimits/usage-limits';
import usePostRequestUpgrade from '../../../hooks/utils/usePostRequestUpgrade';
import Fees from '../../Fees/fees';
import Charges from '../../Charges/charges';
import { TaxFormInputs } from '../../Charges/modal-tax';
import { useLoading } from '../../../context/LoadingContext';
import FirstStep, { FirstStepFormInputs } from './first-step';
import ModalCreationSteps from './ModalCreationSteps';

type UsageLimitsId = {
  // eslint-disable-next-line camelcase
  usage_limits_id: number;
}

type ChargesAffinityGroup = {
  charges: ChargesFormInputs[];
}

export type CardAffinityGroup = {
  // eslint-disable-next-line camelcase
  affinity_group: FirstStepFormInputs;
  charges: ChargesAffinityGroup;
  fees: FeesFormInputs[];
  // eslint-disable-next-line camelcase
  transaction_limits: LimitsFormInputs;
  // eslint-disable-next-line camelcase
  usage_limits: UsageLimitsId;
}

type FirstStepProps = {
  goToPrevious: () => void;
  firstData: FirstStepFormInputs;
  fees: FeesFormInputs[];
  charges: ChargesFormInputs[];
  limit: LimitsFormInputs;
  usageLimit: UsageLimits;
};

const FiveStep: React.FC<FirstStepProps> = ({ goToPrevious, firstData, fees, charges, limit, usageLimit }) => {
  const navigate = useNavigate();
  const { issuer } = useParams<{ issuer: string }>();
  const { postLoading, executePost } = usePostRequestUpgrade({ path: 'v1/card_affinity_groups/full' });
  const { setLoading, setSuccess, setErrors } = useLoading();
  const [modalOpen, setModalOpen] = useState(false);
  const [cardAffinityGroupFinal, setCardAffinityGroupFinal] = useState<CardAffinityGroup>(null);

  const divStyle = {
    borderLeft: '4px solid #a368fc',
    padding: '10px',
    backgroundColor: '#f9f0ff'
  };

  const parseICashOutAndLoad = (data: ICashOutAndLoad): ICashOutAndLoad => ({
    ...data,
    max_amount: Number(data?.max_amount),
    max_daily_amount: Number(data?.max_daily_amount),
    max_monthly_amount: Number(data?.max_monthly_amount),
    min_amount: Number(data?.min_amount),
  });

  const parseICashback = (data: ICashback): ICashback => ({
    ...data,
    max_amount: Number(data?.max_amount),
    min_amount: Number(data?.min_amount),
  });

  const parseIPurchase = (data: IPurchase): IPurchase => ({
    ...data,
    min_amount: Number(data?.min_amount),
  });

  const parseLimits = (limits: LimitsFormInputs): LimitsFormInputs => ({
    ...limits,
    cash_out: parseICashOutAndLoad(limits?.cash_out),
    cashback: parseICashback(limits?.cashback),
    load: parseICashOutAndLoad(limits?.load),
    purchase: parseIPurchase(limits?.purchase),
  });


  const parseFees = (fees: FeesFormInputs[]): FeesFormInputs[] => {
    return fees.map((fee) => ({
      ...fee,
      fixed_fee: Number(fee?.fixed_fee),
      fee: Number(fee?.fee),
      tax_fee: Number(fee?.tax_fee),
    }));
  };

  const parseTaxes = (taxes: TaxFormInputs[]): TaxFormInputs[] => {
    return taxes.map((tax) => ({
      ...tax,
      value: Number(tax?.value),
      exceptions: tax?.exceptions?.map((exception) => ({
        ...exception,
      })),
    }));
  };

  const parseCharges = (charges: ChargesFormInputs[]): ChargesFormInputs[] => {
    return charges.map((charge) => ({
      ...charge,
      value: Number(charge?.value),
      taxes: parseTaxes(charge?.taxes),
    }));
  };

  const createCardAffGroup = async () => {

    const cardAffinityGroupFinal: CardAffinityGroup = {
      affinity_group: {
        ...firstData,
        emboss: {
          ...firstData?.emboss,
          recording_code: Number(firstData?.emboss?.recording_code)
        }
      },
      charges: { charges: parseCharges(charges) },
      fees: parseFees(fees),
      transaction_limits: parseLimits(limit),
      usage_limits: { usage_limits_id: Number(usageLimit?.id) }
    }

    setCardAffinityGroupFinal(cardAffinityGroupFinal);
    setModalOpen(true);
  }

  return (
    <>
      <Row gutter={24}>
        <Col span={21} offset={4}>
          <FirstStep
            firstStepData={firstData}
            action={'view'}
          />
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={14} offset={4} style={{marginTop: '20px'}}>
          <Fees
            fees={ fees }
            action = { 'view' }
          />
        </Col>
        <Col span={14} offset={4} style={{marginTop: '20px'}}>
          <Charges
            charges={ charges }
            action = { 'view' }
          />
        </Col>
        <Col span={14} offset={4} style={{marginTop: '20px'}}>
          <Typography.Title level={3}>Limitador de uso</Typography.Title>
          <div style={divStyle}>
            { usageLimit ? usageLimit?.name : 'Sin limitador de uso...' }
          </div>
        </Col>
        <Col span={14} offset={4} style={{marginTop: '20px'}}>
          <Limits
            limit = { limit }
            action = { 'view' }
          />
        </Col>
      </Row>
      <Col span={32} style={{ textAlign: 'center', marginTop: '40px' }} >
        <Space>
          <Button onClick={ goToPrevious } >
            Anterior
          </Button>
          <Button loading={postLoading} type="primary" onClick={ createCardAffGroup } >
            Crear
          </Button>
        </Space>
      </Col>
      <ModalCreationSteps
        open={ modalOpen }
        cardAffinityGroup = { cardAffinityGroupFinal }
        onCancel={ () => setModalOpen(false) }
      />
    </>
  )
}

export default FiveStep;